// H15: a 401 burst (#569) makes the attempt-log write fail with the same credential that just failed
// the slot check, so the only evidence of the aborted attempt is lost. Park the row and deliver it
// after the next successful /actions call. Best effort: storage may be unavailable, the queue is
// bounded, and parking never changes what apiCall throws.

const STORAGE_KEY = 'bt-deferred-attempt-logs'
const MAX_PARKED = 25
const MAX_REDELIVERY_ATTEMPTS = 3

export const ATTEMPT_LOG_ENDPOINT = '/actions?cmd=create-message-attempt-log'

export interface ParkedAttemptLog {
    body: Record<string, unknown>
    parkedAt: string
    attempts?: number
}

function isParked(v: unknown): v is ParkedAttemptLog {
    if (typeof v !== 'object' || v === null) return false
    const r = v as Record<string, unknown>
    return typeof r.parkedAt === 'string' && typeof r.body === 'object' && r.body !== null
}

function read(): ParkedAttemptLog[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        const parsed: unknown = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed.filter(isParked) : []
    } catch {
        return []
    }
}

function write(rows: ParkedAttemptLog[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(rows))
    } catch { /* storage unavailable: the row is lost, as before */ }
}

export function parkAttemptLog(body: Record<string, unknown>, now: Date = new Date()): void {
    const rows = read()
    rows.push({ body: { ...body }, parkedAt: now.toISOString() })
    write(rows.slice(-MAX_PARKED))
}

/** The parked body with the original time of the attempt made visible in the log text. */
export function lateBody(row: ParkedAttemptLog): Record<string, unknown> {
    const original = typeof row.body.logMessage === 'string' ? row.body.logMessage : 'attempt log'
    return { ...row.body, logMessage: `${original} (delivered late: written at ${row.parkedAt} while the extension was unauthorized)` }
}

/** Deliver parked rows in order through `send`; stops at the first failure and keeps the rest. */
export async function flushParkedAttemptLogs(send: (body: Record<string, unknown>) => Promise<unknown>): Promise<void> {
    const rows = read()
    if (rows.length === 0) return
    // Clear first so a re-entrant flush (the sends below are themselves /actions calls) cannot double-send.
    write([])
    for (let i = 0; i < rows.length; i++) {
        try {
            await send(lateBody(rows[i]))
        } catch {
            // The failed row is retried a bounded number of times so one poison row cannot block the rest.
            const failed: ParkedAttemptLog = { ...rows[i], attempts: (rows[i].attempts ?? 0) + 1 }
            const keep = failed.attempts !== undefined && failed.attempts >= MAX_REDELIVERY_ATTEMPTS ? [] : [failed]
            write([...keep, ...rows.slice(i + 1), ...read()].slice(-MAX_PARKED))
            return
        }
    }
}

let inFlight: Promise<void> = Promise.resolve()
/** Remember the latest redelivery so callers/tests can wait for it; never rejects. */
export function trackFlush(p: Promise<void>): void {
    inFlight = p.catch(() => undefined)
}
export function whenParkedLogsSettled(): Promise<void> {
    return inFlight
}
